import type { ReactNode } from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore.js";
import { useTopbarStore } from "../stores/topbarStore.js";
import { useAutoLock } from "../hooks/useAutoLock.js";
import { MenuIcon, LockIcon, CloseIcon } from "./icons.js";

interface NavItem {
  path: string;
  label: string;
  adminOnly?: boolean;
  ownerOnly?: boolean;
  multiStoreOnly?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "よく使う",
    items: [
      { path: "/", label: "レジ" },
      { path: "/sales", label: "売上履歴" },
      { path: "/daily-summary", label: "日次集計" },
      { path: "/admin/all-stores-summary", label: "全店舗集計", multiStoreOnly: true },
    ],
  },
  {
    title: "管理",
    items: [
      { path: "/admin/products", label: "商品管理", ownerOnly: true },
      { path: "/admin/categories", label: "カテゴリー管理" },
      { path: "/admin/payment-methods", label: "支払方法" },
      { path: "/admin/tables", label: "テーブル管理" },
      { path: "/admin/staff", label: "スタッフ", adminOnly: true },
      { path: "/admin/stores", label: "店舗一覧", ownerOnly: true },
      { path: "/admin/store-settings", label: "店舗設定", ownerOnly: true },
      { path: "/admin/printer-settings", label: "プリンター" },
      { path: "/admin/print-jobs", label: "印刷履歴" },
      { path: "/admin/csv-export", label: "CSV出力" },
      { path: "/admin/diagnostics", label: "システム診断" },
    ],
  },
];

export function Layout({ children }: { children: ReactNode }) {
  useAutoLock();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const staff = useAuthStore((state) => state.staff);
  const storeName = useAuthStore((state) => state.storeName);
  const accessibleStores = useAuthStore((state) => state.accessibleStores);
  const logout = useAuthStore((state) => state.logout);
  const topbarContent = useTopbarStore((state) => state.content);

  const isMultiStore = accessibleStores.length > 1;
  const isAdminOrOwner = staff?.role === "ADMIN" || staff?.role === "OWNER";
  const visibleGroups = NAV_GROUPS.map((group) => ({
    title: group.title,
    items: group.items.filter(
      (item) =>
        (!item.adminOnly || isAdminOrOwner) &&
        (!item.ownerOnly || staff?.role === "OWNER") &&
        (!item.multiStoreOnly || isMultiStore),
    ),
  })).filter((group) => group.items.length > 0);

  const goTo = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <div className="app-shell">
      <div className="top-bar">
        <button className="menu-btn" onClick={() => setDrawerOpen(true)} aria-label="メニュー">
          <MenuIcon />
        </button>
        <div className="top-bar__brand">AIレジ</div>
        <div className="top-bar__slot">{topbarContent}</div>
        <div className="top-bar__right">
          <span className="top-bar__who">
            {storeName} ・ {staff?.name}
          </span>
          {isMultiStore && (
            <button className="btn-chip" onClick={() => navigate("/select-store")}>
              店舗切り替え
            </button>
          )}
          <button
            className="lock-btn"
            aria-label="ロック"
            onClick={() => {
              void logout().then(() => navigate("/login", { replace: true }));
            }}
          >
            <LockIcon />
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer__head">
              <span className="drawer__title">メニュー</span>
              <button className="menu-btn" onClick={() => setDrawerOpen(false)} aria-label="閉じる">
                <CloseIcon />
              </button>
            </div>
            {visibleGroups.map((group) => (
              <div key={group.title} className="drawer__group">
                <div className="drawer__group-title">{group.title}</div>
                {group.items.map((item) => (
                  <button
                    key={item.path}
                    className={location.pathname === item.path ? "drawer__item active" : "drawer__item"}
                    onClick={() => goTo(item.path)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="page">{children}</div>
    </div>
  );
}
